import { Stack } from '@inflearn/ds-react';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import type { ReactElement } from 'react';
import { CourseType } from '../../../../resources/@types/courseSchema';
import { MyCoursesLayout } from '../../../components/layout/MyPageLayout/MyCoursesLayout';
import { CourseCardContainer } from '../../../components/pages/my/courses/CourseCardContainer';
import { MyCoursesEmptyStates } from '../../../components/pages/my/courses/MyCoursesEmptyStates';
import { MyCoursesPageFilter } from '../../../components/pages/my/courses/MyCoursesPageFilter';
import { UpcomingEventListContainer } from '../../../components/pages/my/courses/UpcomingEventList';
import { useMyCoursesSearchEvent } from '../../../components/pages/my/courses/hooks/useMyCoursesSearchEvent';
import useUrlStateForMyCourses from '../../../components/pages/my/courses/hooks/useUrlStateForMyCourses';
import useViewMyCoursesPageEvent from '../../../components/pages/my/courses/hooks/useViewMyCoursesPageEvent';
import { useClientApiVoucherMyQuery } from '../../../components/pages/my/courses/queries/useClientApiVoucherMyQuery';
import { useVoucherMyChallengesQuery } from '../../../queries/useVoucherMyChallengesQuery';

export const getStaticProps = async ({ locale }: { locale: string }) => ({
  props: {
    ...(await serverSideTranslations(locale, ['common'])),
  },
  revalidate: 60,
});

const DEFAULT_PAGE_SIZE = '42';
function MyCoursesPage() {
  const { currentFilter, ...pageParam } = useUrlStateForMyCourses();
  const {
    data: myCourses,
    isLoading: isLoadingCourses,
    isError: isErrorCourses,
  } = useClientApiVoucherMyQuery({
    query: {
      ...currentFilter,
      pageSize: DEFAULT_PAGE_SIZE,
    },
    options: {
      enabled: currentFilter.courseType !== CourseType.CHALLENGE,
    },
  });

  const {
    data: myChallenges,
    isLoading: isLoadingChallenges,
    isError: isErrorChallenges,
  } = useVoucherMyChallengesQuery({
    query: {
      ...currentFilter,
      pageSize: DEFAULT_PAGE_SIZE,
    },
    options: {
      enabled: currentFilter.courseType === CourseType.CHALLENGE,
    },
  });

  const myVoucher = currentFilter.courseType === CourseType.CHALLENGE ? myChallenges : myCourses;
  const isLoading =
    currentFilter.courseType === CourseType.CHALLENGE ? isLoadingChallenges : isLoadingCourses;
  const isError =
    currentFilter.courseType === CourseType.CHALLENGE ? isErrorChallenges : isErrorCourses;

  useViewMyCoursesPageEvent({
    myVoucher,
    courseType: currentFilter.courseType,
  });
  useMyCoursesSearchEvent({
    filterValue: currentFilter,
    myVoucher,
    isSearching: isLoading,
  });

  return (
    <Stack spacing={24}>
      <MyCoursesPageFilter filterState={currentFilter} changeFilter={pageParam.setValue} />
      <UpcomingEventListContainer courseType={currentFilter.courseType} />
      <CourseCardContainer
        isLoading={isLoading}
        isError={isError}
        items={myVoucher?.items}
        totalPage={myVoucher?.totalPage || 0}
        pageNumber={myVoucher?.pageNumber || 0}
        courseType={currentFilter.courseType}
        emptyState={
          currentFilter.searchKeyword ? (
            <MyCoursesEmptyStates.NoSearchResultState
              onReset={() =>
                pageParam.setValue({
                  ...currentFilter,
                  searchKeyword: '',
                })
              }
            />
          ) : (
            <MyCoursesEmptyStates.EmptyVoucherState />
          )
        }
        errorState={<MyCoursesEmptyStates.ErrorState />}
        changePage={(value) => pageParam.setValue({ ...currentFilter, pageNumber: `${value}` })}
      />
    </Stack>
  );
}

function MyCoursesPageLayout({ children, pathname }: { children: ReactElement; pathname: string }) {
  const { t } = useTranslation();
  return (
    <MyCoursesLayout
      pathname={pathname}
      title={t('메타.내학습.제목')}
      description={t('메타.내학습.설명')}
    >
      {children}
    </MyCoursesLayout>
  );
}

MyCoursesPage.getLayout = (page: ReactElement, pathname: string) => (
  <MyCoursesPageLayout pathname={pathname}>{page}</MyCoursesPageLayout>
);

export default MyCoursesPage;
